import Label from '@/components/basicComponents/label';
import { useResponsive } from '@/hooks/use-responsive';
import { buildTestId } from '@/lib/utils/testIds';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { useEffect } from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { IRenderTemplatePage, IRenderTemplateRow } from '../../types';
import { allRepeatedComponentsFailDependsOnCheck, evaluateDependencyItem, shouldRenderEmptyComponent } from '../../utils';
import { CheckboxGroupItem } from './checkboxGroupItem';

interface CheckboxGroupComponentProps {
  row: IRenderTemplateRow;
  pageName?: string;
  pageData?: IRenderTemplatePage;
  ignoreRequired: boolean;
}

export const CheckboxGroupComponent = ({ row, pageName = '', pageData, ignoreRequired }: CheckboxGroupComponentProps) => {
  const { isMobile } = useResponsive();
  const {
    control,
    unregister,
    watch,
    trigger,
    register,
    setValue,
    formState: { errors }
  } = useFormContext();
  const rows = pageData?.rows || [];
  const { label, config, validation, dependsOn, name } = row;
  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      // Check if repeated components exist in the page and all fail dependsOn check before unregistering form values
      if (allRepeatedComponentsFailDependsOnCheck(rows, name, pageName, watch)) {
        if ('sections' in config) {
          config?.sections?.forEach((section: any) => {
            section?.items?.forEach((item: any) => {
              unregister(`${pageName}.${item.name}`);
            });
          });
        }
        if ('items' in config) {
          config?.items?.forEach((item: any) => {
            unregister(`${pageName}.${item.name}`);
          });
        }
      }
    } else {
      if ('sections' in config) {
        config?.sections?.forEach((section: any) => {
          section?.items?.forEach((item: any) => {
            if (
              (watch(`${pageName}.${item.name}`) === undefined && item.defaultValue !== undefined) ||
              watch(`${pageName}.${item.name}`) === item.defaultValue
            ) {
              setValue(`${pageName}.${item.name}`, item.defaultValue);
            }
          });
        });
      }
      if ('items' in config) {
        config?.items?.forEach((item: any) => {
          if (
            (watch(`${pageName}.${item.name}`) === undefined && item.defaultValue !== undefined) ||
            watch(`${pageName}.${item.name}`) === item.defaultValue
          ) {
            setValue(`${pageName}.${item.name}`, item.defaultValue);
          }
        });
      }
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  const groupMetaField = `${pageName}.${row.name}__meta`;

  const groupErrors = get(errors, groupMetaField);

  return (
    <div>
      <input type="hidden" {...register(groupMetaField)} />
      {groupErrors && <ErrorText>{groupErrors.root?.message || groupErrors.message}</ErrorText>}
      {label && (
        <Label size="md" required={!!validation?.required && !ignoreRequired}>
          {label}
        </Label>
      )}
      <SectionsWrapper>
        {'sections' in config &&
          config?.sections?.map((section: any, sectionIndex: number) => (
            <div key={guid()}>
              {section?.label && <Label size="md">{section.label ?? ''}</Label>}
              <input type="hidden" {...register(`${groupMetaField}.section${sectionIndex}`)} />

              {get(errors, `${groupMetaField}.section${sectionIndex}`) && (
                <ErrorText>{get(errors, `${groupMetaField}.section${sectionIndex}.message`) as string}</ErrorText>
              )}
              {'items' in section && (
                <CheckboxesWrapper isMobile={isMobile}>
                  {section?.items?.map((item: any, index: number) => (
                    <Controller
                      key={item.name}
                      name={`${pageName}.${item.name}`}
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <CheckboxGroupItem
                            key={item.name}
                            index={index}
                            value={!!value}
                            title={item.label}
                            description={item.description}
                            isRequiredField={!!item?.validation?.required && !ignoreRequired}
                            testId={item.name ? buildTestId('signature-packet', pageName, item.name) : undefined}
                            onChangeValue={() => {
                              // Handle single selection mode
                              if (section.selectType === 'single' && !value) {
                                // Unselect all other checkboxes in this section
                                section.items.forEach((otherItem: any) => {
                                  if (otherItem.name !== item.name) {
                                    setValue(`${pageName}.${otherItem.name}`, false);
                                  }
                                });
                              }
                              onChange(!value);
                              // trigger();
                            }}
                          />
                          {error?.message && <ErrorText>{error?.message}</ErrorText>}
                        </>
                      )}
                    />
                  ))}
                </CheckboxesWrapper>
              )}
            </div>
          ))}
      </SectionsWrapper>
      <CheckboxesWrapper isMobile={isMobile}>
        {'items' in config &&
          config?.items?.map((item: any, index: number) => (
            <Controller
              key={item.name}
              name={`${pageName}.${item.name}`}
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <CheckboxGroupItem
                    key={item.name}
                    index={index}
                    value={!!value}
                    title={item.label}
                    description={item.description}
                    isRequiredField={!!item?.validation?.required && !ignoreRequired}
                    testId={item.name ? buildTestId('signature-packet', pageName, item.name) : undefined}
                    onChangeValue={() => {
                      // Handle single selection mode
                      if (config.selectType === 'single' && !value) {
                        // Unselect all other checkboxes in this group
                        config.items.forEach((otherItem: any) => {
                          if (otherItem.name !== item.name) {
                            setValue(`${pageName}.${otherItem.name}`, false);
                          }
                        });
                      }
                      onChange(!value);
                      // trigger();
                    }}
                  />
                  {error?.message && <ErrorText>{error?.message}</ErrorText>}
                </>
              )}
            />
          ))}
      </CheckboxesWrapper>
    </div>
  );
};

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxesWrapper = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '10px' : '20px')};
`;

const ErrorText = styled.p`
  color: ${themeVar('context.error')};
  font-family: Blinker;
  font-weight: 400;
  font-size: 16px;
  line-height: 22.4px;
`;
